import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Phone, Star } from "lucide-react";
import Logo from "./Logo";
import CalendlyButton from "./CalendlyButton";
import { SOCIALS } from "../../lib/socials";

export default function Footer() {
  return (
    <footer className="bg-[#111111] border-t border-[#d4af37]/10 pt-20 pb-10" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="space-y-6 lg:col-span-1">
            <Logo />
            <p className="text-[13px] leading-relaxed text-white/60 max-w-[230px]">
              Luxury geofencing and programmatic intelligence for brands that demand
              precision and performance.
            </p>
            <div className="flex items-center gap-4">
              <a aria-label="LinkedIn" href={SOCIALS.linkedin.url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#d4af37] transition" data-testid="social-linkedin">
                <Linkedin size={16} strokeWidth={1.4} />
              </a>
              <a aria-label="Instagram" href={SOCIALS.instagram.url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#d4af37] transition" data-testid="social-instagram">
                <Instagram size={16} strokeWidth={1.4} />
              </a>
              <a aria-label="Facebook" href={SOCIALS.facebook.url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-[#d4af37] transition" data-testid="social-facebook">
                <Facebook size={16} strokeWidth={1.4} />
              </a>
            </div>
          </div>

          <FooterCol
            title="Services"
            items={[
              { label: "Geofencing Campaigns", to: "/geofencing-101" },
              { label: "Programmatic Advertising", to: "/services" },
              { label: "Audience Retargeting", to: "/services" },
              { label: "Data & Analytics", to: "/services" },
              { label: "Campaign Strategy", to: "/services" },
            ]}
          />
          <FooterCol
            title="Company"
            items={[
              { label: "About", to: "/about" },
              { label: "Case Studies", to: "/case-studies" },
              { label: "Testimonials", to: "/about" },
              { label: "Contact", to: "/contact" },
            ]}
          />
          <FooterCol
            title="Resources"
            items={[
              { label: "Start a Campaign", to: "/campaign-builder" },
              { label: "Geofencing 101", to: "/geofencing-101" },
              { label: "Client Portal", to: "/login" },
              { label: "FAQs", to: "/geofencing-101#faq" },
            ]}
          />

          {/* CTA */}
          <div className="space-y-6">
            <div className="kicker">Let's build</div>
            <h3 className="h-display text-[28px] leading-[1.0] text-white">
              YOUR NEXT<br />CAMPAIGN.
            </h3>
            <Link to="/contact" className="kicker text-[#d4af37] hover:text-[#f2c66d] inline-block" data-testid="footer-contact-link">
              Or send us a message →
            </Link>
            <CalendlyButton testId="footer-cta-button">Book a Strategy Call</CalendlyButton>
            <a href="tel:2533318487" className="flex items-center gap-2 text-[#d4af37] tracking-widest text-[12px]" data-testid="footer-phone-link">
              <Phone size={14} strokeWidth={1.4} /> 253-331-8487
            </a>
            <a
              href="https://g.page/r/Cf1jEdpNJ_I1EBM/review"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white/70 hover:text-[#d4af37] tracking-widest text-[12px] transition"
              data-testid="footer-google-review-link"
            >
              <Star size={14} strokeWidth={1.4} className="text-[#d4af37]" /> Leave Us A Google Review
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] tracking-wider text-white/40">
          <p>© {new Date().getFullYear()} Rae Enterprise LLC. All rights reserved.</p>
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-[#d4af37]" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-[#d4af37]" data-testid="footer-terms-link">Terms of Service</Link>
            <Link to="/cookie-policy" className="hover:text-[#d4af37]" data-testid="footer-cookies-link">Cookie Policy</Link>
            <Link to="/do-not-sell" className="hover:text-[#d4af37]" data-testid="footer-do-not-sell-link">Do Not Sell or Share My Personal Information</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, items }) {
  return (
    <div>
      <h4 className="text-[#d4af37] text-[11px] tracking-[0.25em] uppercase font-medium mb-5">{title}</h4>
      <ul className="space-y-3">
        {items.map((it) => (
          <li key={it.label}>
            <Link
              to={it.to}
              onClick={() => window.scrollTo(0, 0)}
              className="text-[13px] text-white/70 hover:text-white hover:translate-x-1 inline-block transition"
              data-testid={`footer-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {it.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
